import dayjs from 'dayjs'
import advancedFormat from 'dayjs/plugin/advancedFormat.js'
import customParseFormat from 'dayjs/plugin/customParseFormat.js'
import minMax from 'dayjs/plugin/minMax.js'
import relativeTime from 'dayjs/plugin/relativeTime.js'
import timezone from 'dayjs/plugin/timezone.js'
import utc from 'dayjs/plugin/utc.js'

// Load dayjs plugins
dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(customParseFormat)
dayjs.extend(relativeTime)
dayjs.extend(advancedFormat)
dayjs.extend(minMax)

export type FvDateInput = dayjs.ConfigType | FvDate
export type FvManipulateUnit = dayjs.ManipulateType
export type FvDateUnit = dayjs.UnitType
export type FvOpUnit = dayjs.OpUnitType

export class FvDate {
  private readonly _date: dayjs.Dayjs

  private constructor(initialDate: dayjs.Dayjs) {
    this._date = initialDate
  }

  /**
   * Creates a new FvDate instance in LOCAL time mode.
   * @param input Optional input (Date, string, number, FvDate). Defaults to now.
   * @param fmt Optional format string for parsing.
   * @param timezone Optional timezone string.
   * @returns A new FvDate instance (local time).
   */
  static create(input?: FvDateInput, fmt?: string, timezone?: string): FvDate {
    let instance: dayjs.Dayjs

    if (input instanceof FvDate) {
      instance = input._date.clone() // Clone the internal dayjs object
    } else if (timezone) {
      // If timezone is provided, parse in that timezone
      instance = dayjs.tz(input, fmt, timezone)
    } else {
      // Otherwise, parse in local time
      instance = dayjs(input, fmt)
    }

    // Check for invalid date
    if (!instance.isValid()) {
      instance = dayjs(NaN) // Create an invalid dayjs object
    }

    return new FvDate(instance)
  }

  /**
   * Creates a new FvDate instance in UTC mode.
   * @param input Optional input (Date, string, number, FvDate). Defaults to now (UTC).
   *              If string has no offset, it's interpreted as UTC.
   * @param fmt Optional format string for parsing.
   * @returns A new FvDate instance (UTC).
   */
  static utc(input?: FvDateInput, fmt?: string): FvDate {
    let instance: dayjs.Dayjs

    if (input instanceof FvDate) {
      instance = input._date.clone().utc() // Clone and ensure UTC mode
    } else {
      instance = dayjs.utc(input, fmt)
    }

    // Check for invalid date
    if (!instance.isValid()) {
      instance = dayjs.utc(NaN) // Create an invalid UTC dayjs object
    }
    return new FvDate(instance)
  }

  // --- Instance Methods ---

  /**
   * Returns a **new** FvDate object with the specified amount of time added.
   * Preserves the UTC/local mode of the original instance.
   * @param amount The number of units to add.
   * @param unit The unit of time (e.g., 'days', 'months', 'years').
   * @returns A new FvDate instance.
   */
  add(amount: number, unit: FvManipulateUnit): FvDate {
    const newDate = this._date.add(amount, unit)
    return new FvDate(newDate)
  }

  /**
   * Returns a **new** FvDate object with the specified amount of time subtracted.
   * Preserves the UTC/local mode of the original instance.
   * @param amount The number of units to subtract.
   * @param unit The unit of time (e.g., 'days', 'months', 'years').
   * @returns A new FvDate instance.
   */
  subtract(amount: number, unit: FvManipulateUnit): FvDate {
    const newDate = this._date.subtract(amount, unit)
    return new FvDate(newDate)
  }

  /**
   * Returns the native Date object.
   * Warning: This loses the UTC/local context managed by FvDate/Dayjs.
   * Use it primarily for compatibility with APIs expecting a Date.
   */
  toDate(): Date {
    return this._date.toDate()
  }

  toISOString(): string {
    return this._date.toISOString()
  }

  /**
   * Returns the internal Dayjs object.
   * Useful for accessing Dayjs-specific methods not exposed by FvDate.
   */
  toDayjs(): dayjs.Dayjs {
    return this._date
  }

  clone(): FvDate {
    return FvDate.create(this)
  }

  /**
   * Formats the date according to the given format string.
   * Uses Dayjs formatting tokens: https://day.js.org/docs/en/display/format
   * @param formatString The format string.
   * @returns The formatted date string.
   */
  format(formatString?: string): string {
    return this._date.format(formatString)
  }

  toString(): string {
    return this.format()
  }

  /**
   * Returns a **new** FvDate object representing the start of the given unit of time.
   * Preserves the UTC/local mode.
   * @param unit The unit of time (e.g., 'day', 'week', 'month').
   * @returns A new FvDate instance.
   */
  startOf(unit: FvOpUnit): FvDate {
    return new FvDate(this._date.startOf(unit))
  }

  /**
   * Returns a **new** FvDate object representing the end of the given unit of time.
   * Preserves the UTC/local mode.
   * @param unit The unit of time (e.g., 'day', 'week', 'month').
   * @returns A new FvDate instance.
   */
  endOf(unit: FvOpUnit): FvDate {
    return new FvDate(this._date.endOf(unit))
  }

  /**
   * Returns a **new** FvDate object with the specified unit set to the given value.
   * Preserves the UTC/local mode.
   * Note: Dayjs month is 0-indexed (0=Jan, 11=Dec).
   * @param unit The unit to set (e.g., 'year', 'month', 'date').
   * @param value The value to set the unit to.
   * @returns A new FvDate instance.
   */
  set(unit: FvDateUnit, value: number): FvDate {
    return new FvDate(this._date.set(unit, value))
  }

  /**
   * Gets the value of the specified unit.
   * Note: Dayjs month is 0-indexed (0=Jan, 11=Dec).
   * @param unit The unit to get (e.g., 'year', 'month', 'date', 'day').
   * @returns The value of the unit.
   */
  get(unit: FvDateUnit): number {
    return this._date.get(unit)
  }

  /**
   * Checks if the current FvDate instance is after the other date.
   * @param otherDate The date to compare against.
   * @param unit Optional granularity for comparison (e.g., 'day', 'month'). Defaults to milliseconds.
   * @returns True if the current instance is after the other date, false otherwise.
   */
  isAfter(otherDate: FvDateInput, unit?: FvOpUnit): boolean {
    const other = FvDate.create(otherDate).toDayjs() // Ensure comparison with Dayjs object
    return this._date.isAfter(other, unit)
  }

  /**
   * Checks if the current FvDate instance is before the other date.
   * @param otherDate The date to compare against.
   * @param unit Optional granularity for comparison (e.g., 'day', 'month'). Defaults to milliseconds.
   * @returns True if the current instance is before the other date, false otherwise.
   */
  isBefore(otherDate: FvDateInput, unit?: FvOpUnit): boolean {
    const other = FvDate.create(otherDate).toDayjs()
    return this._date.isBefore(other, unit)
  }

  /**
   * Checks if the current FvDate instance is the same as the other date.
   * @param otherDate The date to compare against.
   * @param unit Optional granularity for comparison (e.g., 'day', 'month'). Defaults to milliseconds.
   * @returns True if the current instance is the same as the other date, false otherwise.
   */
  isSame(otherDate: FvDateInput, unit?: FvOpUnit): boolean {
    const other = FvDate.create(otherDate).toDayjs()
    return this._date.isSame(other, unit)
  }

  /**
   * Checks if the current FvDate instance is the same as or before the other date.
   * @param otherDate The date to compare against.
   * @param unit Optional granularity for comparison (e.g., 'day', 'month'). Defaults to milliseconds.
   * @returns True if the current instance is the same as or before the other date.
   */
  isSameOrBefore(otherDate: FvDateInput, unit?: FvOpUnit): boolean {
    const other = FvDate.create(otherDate).toDayjs()
    return this._date.isSame(other, unit) || this._date.isBefore(other, unit)
  }

  /**
   * Checks if the current FvDate instance is the same as or after the other date.
   * @param otherDate The date to compare against.
   * @param unit Optional granularity for comparison (e.g., 'day', 'month'). Defaults to milliseconds.
   * @returns True if the current instance is the same as or after the other date.
   */
  isSameOrAfter(otherDate: FvDateInput, unit?: FvOpUnit): boolean {
    const other = FvDate.create(otherDate).toDayjs()
    return this._date.isSame(other, unit) || this._date.isAfter(other, unit)
  }

  /**
   * Gets the difference between the current FvDate instance and another date in the specified unit.
   * By default, the result is truncated towards zero.
   * @param otherDate The date to compare against.
   * @param unit The unit of time for the difference (e.g., 'days', 'months'). Defaults to milliseconds.
   * @param precise If true, returns a floating-point number; otherwise, an integer. Defaults to false.
   * @returns The difference in the specified unit.
   */
  diff(
    otherDate: FvDateInput,
    unit: FvOpUnit = 'millisecond',
    precise: boolean = false,
  ): number {
    const other = FvDate.create(otherDate).toDayjs()
    return this._date.diff(other, unit, precise)
  }

  // --- Convenience Getters ---
  year(): number {
    return this._date.year()
  }
  month(): number {
    return this._date.month()
  }
  date(): number {
    return this._date.date()
  }
  day(): number {
    return this._date.day()
  } // 0 for Sunday, 1 for Monday...
  hour(): number {
    return this._date.hour()
  }
  minute(): number {
    return this._date.minute()
  }
  second(): number {
    return this._date.second()
  }
  millisecond(): number {
    return this._date.millisecond()
  }
  fromNow(withoutSuffix: boolean = false): string {
    return this._date.fromNow(withoutSuffix)
  }

  from(otherDate: FvDateInput, withoutSuffix: boolean = false): string {
    const other = FvDate.create(otherDate).toDayjs()
    return this._date.from(other, withoutSuffix)
  }

  toNow(withoutSuffix: boolean = false): string {
    return this._date.toNow(withoutSuffix)
  }

  to(otherDate: FvDateInput, withoutSuffix: boolean = false): string {
    const other = FvDate.create(otherDate).toDayjs()
    return this._date.to(other, withoutSuffix)
  }

  unix(): number {
    return this._date.unix()
  }

  valueOf(): number {
    return this._date.valueOf()
  }

  utc(): FvDate {
    return new FvDate(this._date.utc())
  }

  local(): FvDate {
    return new FvDate(this._date.local())
  }

  tz(timezone: string): FvDate {
    return new FvDate(this._date.tz(timezone))
  }

  utcOffset(): number {
    return this._date.utcOffset()
  }

  isUtc(): boolean {
    return this._date.isUTC()
  }

  isValid(): boolean {
    return this._date.isValid()
  }
}

export function fvDate(
  input?: FvDateInput,
  fmt?: string,
  timezone?: string,
): FvDate {
  return FvDate.create(input, fmt, timezone)
}

export function fvDateUtc(input?: FvDateInput, fmt?: string): FvDate {
  return FvDate.utc(input, fmt)
}

export function fvDateUnix(input: number): FvDate {
  return FvDate.create(dayjs.unix(input))
}

export function fvDateTz(
  input: Exclude<FvDateInput, FvDate>,
  timezone: string,
): FvDate {
  return FvDate.create(dayjs.tz(input, timezone))
}

export function fvMaxDate(...dates: FvDateInput[]): FvDate {
  const dayjsDates = dates
    .map(d => FvDate.create(d).toDayjs())
    .filter(d => d.isValid())
  if (dayjsDates.length === 0) {
    return FvDate.create(NaN) // Return invalid date if no valid inputs
  }
  const max = dayjs.max(dayjsDates)
  return max ? FvDate.create(max) : FvDate.create(NaN)
}

export function fvMinDate(...dates: FvDateInput[]): FvDate {
  const dayjsDates = dates
    .map(d => FvDate.create(d).toDayjs())
    .filter(d => d.isValid())
  if (dayjsDates.length === 0) {
    return FvDate.create(NaN) // Return invalid date if no valid inputs
  }
  const min = dayjs.min(dayjsDates)
  return min ? FvDate.create(min) : FvDate.create(NaN)
}

export function chunkDateRange(
  startDate: FvDateInput,
  endDate: FvDateInput,
  numberOfChunks: number,
): { start: FvDate; end: FvDate }[] {
  const start = FvDate.create(startDate).toDate()
  const end = FvDate.create(endDate).toDate()

  const intervalLength = (end.getTime() - start.getTime()) / numberOfChunks
  return [...new Array(numberOfChunks)].map((_, i) => ({
    start: FvDate.create(start.getTime() + i * intervalLength),
    end: FvDate.create(start.getTime() + (i + 1) * intervalLength),
  }))
}

export const guessTimezone = (): string => dayjs.tz.guess()

/**
 * Async iterator for date ranges.
 * Allows for async operations to be performed on each date in the range.
 */
export async function* dateRangeIterator(
  startDate: FvDateInput,
  endDate: FvDateInput,
  unit: FvManipulateUnit = 'day',
  step: number = 1,
): AsyncIterableIterator<FvDate> {
  let currentDate = FvDate.create(startDate)
  const end = FvDate.create(endDate)

  while (currentDate.isSameOrBefore(end, unit === 'day' ? 'day' : undefined)) {
    yield currentDate
    currentDate = currentDate.add(step, unit)
  }
}
